/**
 * Kaçış scene: drag jelly worms by head or tail, snake-style, into same-colour gates; the escape order
 * spells a hidden word in the player's language (★ Kelime). Rules live in core/ (pure TS); this file is view + input.
 */
import Phaser from "phaser";
import { exposeBotHook, loadSave, writeSave } from "@kit/boot";
import { currentLang } from "@kit/lang";
import { afterSkip, afterWin, mapBack, mapText, openMap, starLine } from "@kit/map";
import { recordSkip, recordWin } from "@kit/progress";
import { track } from "@kit/telemetry";
import { COLORS, FONT, PANEL, PANEL_2, TEXT_DIM, W, backdrop, button, centerCamera, confetti, floatText, overlay, share, text, topBar } from "@kit/ui";
import { sfx, unlockAudio } from "@shared/audio";
import { planFrom } from "./core/bot";
import { generateLevel } from "./core/generator";
import {
  beginDrag,
  canSkip,
  createGame,
  dragStep,
  endDrag,
  movesLeft,
  probe,
  starsFor,
  wordStar,
  type EscapeInfo,
  type GameState,
  type StepFail,
} from "./core/game";
import { DX, DY, type Cell, type Dir, type End, type Gesture, type KacisLevel } from "./core/types";
import { letters } from "./core/words";
import { t } from "./i18n";
import { drawGateShape, drawStoneShape, drawTube, drawWallShape, type Pt } from "./view";

const ID = "kacis";
const SHARE_URL = "https://kacis.enjoyment.app/?utm_source=share&utm_medium=social";
const GOLD = 0xffd166;

interface Save {
  level: number;
  fails: Record<string, number>;
  stars: number;
  starLevels: number[];
  tipStone: boolean;
}

interface ExitAnim {
  /** centers from the back end to the lead end, then straight out past the gate */
  path: Pt[];
  off: number;
  leadHead: boolean;
  dir: Dir;
  lead: Pt;
}

interface WormView {
  id: number;
  gfx: Phaser.GameObjects.Graphics;
  letter: Phaser.GameObjects.Text;
  vis: Pt[];
  lean: Pt;
  exit?: ExitAnim;
  dirty: boolean;
}

interface Drag {
  pointerId: number;
  worm: number;
  end: End;
  grab: Pt; // pointer offset from the lead cell centre at grab time
}

const ease = (a: number, b: number, k: number): number => a + (b - a) * k;
const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export class KacisScene extends Phaser.Scene {
  private save!: Save;
  private level!: KacisLevel;
  private state!: GameState;
  private H = 1280;
  private cell = 72;
  private ox = 0;
  private oy = 0;
  private startedAt = 0;
  private views = new Map<number, WormView>();
  private dyn: Phaser.GameObjects.GameObject[] = [];
  private gateObjs = new Map<number, Phaser.GameObjects.Container>();
  private stoneObjs = new Map<number, Phaser.GameObjects.Container>();
  private slots: { bg: Phaser.GameObjects.Graphics; t: Phaser.GameObjects.Text; x: number; y: number }[] = [];
  private trayLabel!: Phaser.GameObjects.Text;
  private bar!: { title: Phaser.GameObjects.Text; value: Phaser.GameObjects.Text };
  private drag: Drag | null = null;
  private overlayBox?: Phaser.GameObjects.Container;
  private finished = false;
  private autoplaying = false;
  private lastNope = 0;
  private lastTick = 0;
  private pendingEnd = 0;
  private levelArg?: number;

  constructor() {
    super("kacis");
  }

  init(data?: { level?: number }): void {
    // Re-entered from the level map: forget objects of the previous visit.
    this.levelArg = data?.level;
    this.views = new Map();
    this.dyn = [];
    this.gateObjs = new Map();
    this.stoneObjs = new Map();
    this.slots = [];
    this.drag = null;
    this.overlayBox = undefined;
    this.finished = false;
    this.autoplaying = false;
    this.pendingEnd = 0;
  }

  create(): void {
    centerCamera(this);
    backdrop(this, { light: 0xff595e });
    this.save = loadSave<Save>(ID, { level: 1, fails: {}, stars: 0, starLevels: [], tipStone: false });
    this.makeTextures();
    this.bar = topBar(this, t("title"), t("moves"), mapBack(this, ID));
    this.add.text(W - 36, 128, t("left"), { fontFamily: FONT, fontSize: "18px", color: TEXT_DIM }).setOrigin(1, 0.5);
    this.input.on("pointerdown", this.onDown, this);
    this.input.on("pointermove", this.onMove, this);
    this.input.on("pointerup", this.onUp, this);
    this.input.on("pointerupoutside", this.onUp, this);
    this.scale.on("resize", this.onResize, this);
    this.events.once("shutdown", () => this.scale.off("resize", this.onResize, this));
    exposeBotHook({ state: () => this.snapshot(), autoplay: () => this.autoplay() });
    this.startLevel(this.levelArg ?? this.save.level);
  }

  // ------------------------------------------------------------------ setup

  private makeTextures(): void {
    if (this.textures.exists("kacis-dot")) return;
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 1).fillCircle(8, 8, 8);
    g.generateTexture("kacis-dot", 16, 16);
    g.destroy();
  }

  private add_<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.dyn.push(o);
    return o;
  }

  private clearView(): void {
    this.tweens.killAll();
    this.time.removeAllEvents();
    for (const o of this.dyn) o.destroy();
    this.dyn = [];
    this.views.clear();
    this.gateObjs.clear();
    this.stoneObjs.clear();
    this.slots = [];
    this.overlayBox?.destroy();
    this.overlayBox = undefined;
  }

  private startLevel(n: number): void {
    this.clearView();
    this.level = generateLevel(n, currentLang());
    this.state = createGame(this.level);
    this.save.level = n;
    writeSave(ID, this.save);
    this.drag = null;
    this.finished = false;
    this.startedAt = Date.now();
    this.pendingEnd = 0;
    this.bar.title.setText(`${t("title")} · ${n}`);
    this.buildView();
    track("level_start", { level: n, word_len: this.level.word.length, budget: this.level.budget });
  }

  private onResize(): void {
    if (!this.state) return;
    this.buildView(true);
  }

  private center(x: number, y: number): Pt {
    return { x: this.ox + (x + 0.5) * this.cell, y: this.oy + (y + 0.5) * this.cell };
  }

  /** (Re)create every board object from the current state (also used on resize). */
  private buildView(keepOverlay = false): void {
    const keep = keepOverlay ? this.overlayBox : undefined;
    for (const o of this.dyn) o.destroy();
    this.dyn = [];
    this.views.clear();
    this.gateObjs.clear();
    this.stoneObjs.clear();
    this.slots = [];
    if (!keep) this.overlayBox?.destroy();
    this.tweens.killAll();
    const s = this.state;
    const lv = this.level;
    this.H = this.scale.height;
    const H = this.H;
    const regionTop = 246;
    const regionBottom = H - 236;
    const availH = regionBottom - regionTop - 62;
    this.cell = Math.max(72, Math.floor(Math.min((W - 72) / lv.cols, availH / lv.rows, 112)));
    const bw = lv.cols * this.cell;
    const bh = lv.rows * this.cell;
    this.ox = Math.round((W - bw) / 2);
    this.oy = Math.round(regionTop + 31 + (regionBottom - regionTop - 62 - bh) / 2);

    // hint row
    const ls = letters(lv.hint);
    const tw = 52;
    const gap = 8;
    const rowW = ls.length * tw + (ls.length - 1) * gap;
    const label = this.add_(this.add.text(36, 206, t("scrambled"), { fontFamily: FONT, fontSize: "22px", color: TEXT_DIM }).setOrigin(0, 0.5));
    label.setDepth(1);
    ls.forEach((ch, i) => {
      const x = W - 36 - rowW + i * (tw + gap) + tw / 2;
      const g = this.add_(this.add.graphics().setDepth(1));
      g.fillStyle(PANEL_2, 1).fillRoundedRect(x - tw / 2, 206 - 29, tw, 58, 12);
      g.lineStyle(2, 0x3a4170, 1).strokeRoundedRect(x - tw / 2, 206 - 29, tw, 58, 12);
      this.add_(text(this, x, 207, ch, 32).setDepth(1));
    });

    // board
    const panel = this.add_(this.add.graphics().setDepth(0));
    panel.fillStyle(0x151833, 1).fillRoundedRect(this.ox - 14, this.oy - 14, bw + 28, bh + 28, 26);
    panel.fillStyle(PANEL, 1).fillRoundedRect(this.ox - 8, this.oy - 8, bw + 16, bh + 16, 22);
    for (let y = 0; y < lv.rows; y++)
      for (let x = 0; x < lv.cols; x++) {
        const c = this.center(x, y);
        panel.fillStyle((x + y) % 2 === 0 ? PANEL_2 : 0x2c3260, 1).fillRoundedRect(c.x - this.cell / 2 + 2, c.y - this.cell / 2 + 2, this.cell - 4, this.cell - 4, 12);
      }

    // walls and numbered stones
    for (const st of lv.stones) {
      const id = st.y * lv.cols + st.x;
      if (st.n > 0 && st.n <= s.escaped) continue;
      const c = this.center(st.x, st.y);
      const g = this.add.graphics();
      const items: Phaser.GameObjects.GameObject[] = [g];
      if (st.n === 0) drawWallShape(g, this.cell);
      else {
        drawStoneShape(g, this.cell);
        items.push(text(this, 0, 2, String(st.n), Math.round(this.cell * 0.46), "#ffffff"));
        (items[1] as Phaser.GameObjects.Text).setStroke("#3a2412", 5);
      }
      const box = this.add_(this.add.container(c.x, c.y, items).setDepth(2));
      this.stoneObjs.set(id, box);
    }

    // gates
    const out = this.cell * 0.5;
    for (const g of lv.gates) {
      const c = this.center(g.x, g.y);
      const gx = c.x + DX[g.dir] * out;
      const gy = c.y + DY[g.dir] * out;
      const shape = this.add.graphics();
      drawGateShape(shape, this.cell, COLORS[g.color]!);
      const box = this.add_(this.add.container(gx, gy, [shape]).setDepth(5));
      box.setRotation([Math.PI, -Math.PI / 2, 0, Math.PI / 2][g.dir]!);
      this.gateObjs.set((g.y * lv.cols + g.x) * 4 + g.dir, box);
    }

    // worms
    for (const w of s.worms) {
      if (!w.alive) continue;
      const gfx = this.add_(this.add.graphics().setDepth(3));
      const lt = this.add_(
        this.add
          .text(0, 0, w.letter, { fontFamily: FONT, fontSize: `${Math.round(this.cell * 0.44)}px`, color: "#ffffff", fontStyle: "bold", stroke: "#1b1f3a", strokeThickness: 6 })
          .setOrigin(0.5)
          .setDepth(4),
      );
      const vis = w.cells.map((c) => this.center(c.x, c.y));
      const v: WormView = { id: w.id, gfx, letter: lt, vis, lean: { x: 0, y: 0 }, dirty: true };
      this.views.set(w.id, v);
      this.drawWorm(v);
    }

    // tray
    const n = ls.length;
    const slotW = 84;
    const slotGap = 14;
    const totalW = n * slotW + (n - 1) * slotGap;
    const ty = H - 150;
    this.trayLabel = this.add_(text(this, W / 2, H - 216, t("word"), 22, TEXT_DIM, false).setDepth(1));
    for (let i = 0; i < n; i++) {
      const x = W / 2 - totalW / 2 + slotW / 2 + i * (slotW + slotGap);
      const bg = this.add_(this.add.graphics().setDepth(1));
      bg.fillStyle(0x151833, 1).fillRoundedRect(x - slotW / 2, ty - 46, slotW, 92, 18);
      bg.lineStyle(3, 0x3a4170, 1).strokeRoundedRect(x - slotW / 2, ty - 46, slotW, 92, 18);
      const t = this.add_(text(this, x, ty, "", 48).setDepth(2));
      this.slots.push({ bg, t, x, y: ty });
    }
    // slots already filled (resize / rebuild)
    s.order.forEach((ch, i) => this.fillSlot(i, ch, COLORS[s.worms[s.escapeIds[i]!]!.color]!, false));
    this.refreshTrayLabel();

    // tips
    const tip = this.tipText();
    if (tip) this.add_(text(this, W / 2, H - 252, tip, 21, "#b8bee6", false).setDepth(1).setWordWrapWidth(640));

    // buttons
    const fails = this.save.fails[String(lv.n)] ?? 0;
    const showSkip = canSkip(fails);
    this.add_(button(this, showSkip ? 190 : W / 2, H - 52, showSkip ? 290 : 330, 72, t("restart"), PANEL_2, () => this.retry()).setDepth(1));
    if (showSkip) this.add_(button(this, 530, H - 52, 290, 72, t("skip"), 0x6a4c93, () => this.skip()).setDepth(1));
    this.updateHud();
  }

  private tipText(): string {
    const lv = this.level;
    if (lv.n <= 2) return t("tipDrag");
    if (lv.n <= 4) return t("tipWord");
    if (lv.stones.some((s) => s.n > 0) && !this.save.tipStone) return t("tipStone");
    return "";
  }

  private updateHud(): void {
    const left = movesLeft(this.state);
    this.bar.value.setText(String(left));
    this.bar.value.setColor(left === 0 ? "#ff595e" : left <= 2 ? "#ffb347" : "#ffffff");
  }

  private refreshTrayLabel(): void {
    const s = this.state;
    if (s.status === "won") {
      if (wordStar(s)) this.trayLabel.setText(t("wordStar")).setColor("#ffd166").setFontSize(30);
      else this.trayLabel.setText(t("otherOrder")).setColor(TEXT_DIM);
    } else this.trayLabel.setText(t("wordLen", { n: this.level.word.length })).setColor(TEXT_DIM);
  }

  private fillSlot(i: number, ch: string, color: number, animate = true): void {
    const sl = this.slots[i];
    if (!sl) return;
    sl.bg.clear();
    sl.bg.fillStyle(color, 1).fillRoundedRect(sl.x - 42, sl.y - 46, 84, 92, 18);
    sl.bg.fillStyle(0xffffff, 0.22).fillRoundedRect(sl.x - 36, sl.y - 41, 72, 26, 12);
    sl.bg.lineStyle(3, 0x1b1f3a, 0.6).strokeRoundedRect(sl.x - 42, sl.y - 46, 84, 92, 18);
    sl.t.setText(ch).setStroke("#1b1f3a", 6);
    if (animate) {
      sl.t.setScale(0.4);
      this.tweens.add({ targets: sl.t, scale: 1, duration: 220, ease: "Back.Out" });
    }
  }

  // ------------------------------------------------------------------ drawing

  private mid(v: WormView): number {
    const L = v.vis.length;
    return L > 2 ? Math.floor(L / 2) : 1;
  }

  private drawWorm(v: WormView): void {
    const w = this.state.worms[v.id]!;
    const r = this.cell * 0.37;
    v.gfx.clear();
    let poly: Pt[] = v.vis;
    let scaleAt: ((p: Pt) => number) | undefined;
    let letterAt: Pt;
    let letterScale = 1;
    const ex = v.exit;
    if (ex) {
      poly = this.exitPoly(ex, v.vis.length);
      const border = this.cell * 0.5;
      const o = { x: DX[ex.dir], y: DY[ex.dir] };
      scaleAt = (p) => {
        const e = (p.x - ex.lead.x) * o.x + (p.y - ex.lead.y) * o.y - border;
        return e <= 0 ? 1 : Math.max(0, 1 - e / (this.cell * 0.5));
      };
      const m = this.mid(v);
      const prop = m / Math.max(1, v.vis.length - 1);
      letterAt = poly[Math.round(prop * (poly.length - 1))]!;
      letterScale = scaleAt(letterAt);
    } else {
      letterAt = v.vis[this.mid(v)]!;
    }
    const grabbed = this.drag?.worm === v.id;
    drawTube(v.gfx, poly, { r: r * (grabbed ? 1.06 : 1), color: COLORS[w.color]!, scaleAt, glow: grabbed, eyes: true });
    v.letter.setPosition(letterAt.x, letterAt.y + 1).setScale(letterScale).setAlpha(letterScale > 0.15 ? 1 : 0);
    v.dirty = false;
  }

  /** Head→tail dense polyline of a worm sliding out along its path. */
  private exitPoly(ex: ExitAnim, L: number): Pt[] {
    const c = this.cell;
    const P = ex.path;
    const at = (a: number): Pt => {
      const f = clamp(a / c, 0, P.length - 1);
      const i = Math.min(P.length - 2, Math.floor(f));
      const t = f - i;
      return { x: ease(P[i]!.x, P[i + 1]!.x, t), y: ease(P[i]!.y, P[i + 1]!.y, t) };
    };
    const M = (L - 1) * 6 + 1;
    const pts: Pt[] = [];
    for (let i = 0; i < M; i++) {
      const a = ex.off + ((L - 1) * c * i) / (M - 1);
      pts.push(at(a));
    }
    // pts runs back→front; head must come first
    return ex.leadHead ? pts.reverse() : pts;
  }

  override update(_t: number, dt: number): void {
    if (!this.state) return;
    const k = 1 - Math.exp((-dt / 1000) * 26);
    for (const v of this.views.values()) {
      if (v.exit) {
        if (v.dirty) this.drawWorm(v);
        continue;
      }
      const w = this.state.worms[v.id]!;
      let moving = v.dirty;
      const lastI = w.cells.length - 1;
      const dr = this.drag && this.drag.worm === v.id ? this.drag : null;
      for (let i = 0; i <= lastI; i++) {
        const t = this.center(w.cells[i]!.x, w.cells[i]!.y);
        const isLead = dr && ((dr.end === "head" && i === 0) || (dr.end === "tail" && i === lastI));
        const tx = t.x + (isLead ? v.lean.x : 0);
        const ty = t.y + (isLead ? v.lean.y : 0);
        const p = v.vis[i]!;
        const dx = tx - p.x;
        const dy = ty - p.y;
        if (Math.abs(dx) > 0.25 || Math.abs(dy) > 0.25) {
          p.x += dx * k;
          p.y += dy * k;
          moving = true;
        }
      }
      if (moving) this.drawWorm(v);
    }
  }

  // ------------------------------------------------------------------ input

  private inputOpen(): boolean {
    return !!this.state && this.state.status === "playing" && !this.overlayBox && !this.finished && !this.autoplaying;
  }

  private onDown(p: Phaser.Input.Pointer): void {
    unlockAudio();
    if (!this.inputOpen() || this.drag) return;
    const s = this.state;
    let best: { worm: number; end: End; d: number } | null = null;
    for (const w of s.worms) {
      if (!w.alive) continue;
      const L = w.cells.length;
      w.cells.forEach((c, i) => {
        const ct = this.center(c.x, c.y);
        const d = Math.hypot(p.worldX - ct.x, p.worldY - ct.y);
        if (d > this.cell * 0.66) return;
        const end: End = i <= (L - 1) / 2 ? "head" : "tail";
        if (!best || d < best.d) best = { worm: w.id, end, d };
      });
    }
    const pick = best as { worm: number; end: End; d: number } | null;
    if (!pick) return;
    if (!beginDrag(s, pick.worm, pick.end)) return;
    const w = s.worms[pick.worm]!;
    const lead = w.cells[pick.end === "head" ? 0 : w.cells.length - 1]!;
    const lc = this.center(lead.x, lead.y);
    this.drag = {
      pointerId: p.id,
      worm: pick.worm,
      end: pick.end,
      grab: { x: clamp(p.worldX - lc.x, -this.cell * 0.5, this.cell * 0.5), y: clamp(p.worldY - lc.y, -this.cell * 0.5, this.cell * 0.5) },
    };
    const v = this.views.get(pick.worm);
    if (v) v.dirty = true;
  }

  private onMove(p: Phaser.Input.Pointer): void {
    const d = this.drag;
    if (!d || p.id !== d.pointerId || !p.isDown) return;
    const s = this.state;
    const v = this.views.get(d.worm);
    if (!v) return;
    const tx = p.worldX - d.grab.x;
    const ty = p.worldY - d.grab.y;
    const thr = this.cell * 0.54;
    for (let guard = 0; guard < 4; guard++) {
      const w = s.worms[d.worm]!;
      const lead = w.cells[d.end === "head" ? 0 : w.cells.length - 1]!;
      const c = this.center(lead.x, lead.y);
      const dx = tx - c.x;
      const dy = ty - c.y;
      const opts: { dir: Dir; mag: number }[] = [];
      if (Math.abs(dx) > thr) opts.push({ dir: dx > 0 ? 1 : 3, mag: Math.abs(dx) });
      if (Math.abs(dy) > thr) opts.push({ dir: dy > 0 ? 2 : 0, mag: Math.abs(dy) });
      opts.sort((a, b) => b.mag - a.mag);
      if (!opts.length) break;
      let moved = false;
      let lastFail: { reason: StepFail; at?: Cell; dir: Dir } | null = null;
      for (const o of opts) {
        const pr = probe(s, d.worm, d.end, o.dir);
        if (!pr.ok) {
          lastFail = { reason: pr.reason, at: pr.at, dir: o.dir };
          continue;
        }
        const r = dragStep(s, o.dir);
        moved = true;
        if (r.outcome.ok && r.outcome.kind === "move") this.stepFx();
        if (r.gestureEnded && r.escape) {
          this.onGestureExit(r.escape, d.end, o.dir);
          return;
        }
        break;
      }
      if (!moved) {
        if (lastFail) this.blockedFx(d.worm, lastFail.reason, lastFail.dir, lead);
        break;
      }
    }
    // jelly lean toward the pointer, only toward directions that are open
    const w = s.worms[d.worm]!;
    const lead = w.cells[d.end === "head" ? 0 : w.cells.length - 1]!;
    const c = this.center(lead.x, lead.y);
    const dx = clamp(tx - c.x, -this.cell * 0.6, this.cell * 0.6);
    const dy = clamp(ty - c.y, -this.cell * 0.6, this.cell * 0.6);
    const open = (dir: Dir): boolean => probe(s, d.worm, d.end, dir).ok;
    const lx = Math.abs(dx) < 4 ? 0 : open(dx > 0 ? 1 : 3) ? dx * 0.5 : dx * 0.12;
    const ly = Math.abs(dy) < 4 ? 0 : open(dy > 0 ? 2 : 0) ? dy * 0.5 : dy * 0.12;
    v.lean.x = clamp(lx, -this.cell * 0.3, this.cell * 0.3);
    v.lean.y = clamp(ly, -this.cell * 0.3, this.cell * 0.3);
    v.dirty = true;
  }

  private onUp(p: Phaser.Input.Pointer): void {
    const d = this.drag;
    if (!d || p.id !== d.pointerId) return;
    this.releaseDrag();
  }

  private releaseDrag(): void {
    const d = this.drag;
    if (!d) return;
    const v = this.views.get(d.worm);
    this.drag = null;
    if (v) {
      v.lean.x = 0;
      v.lean.y = 0;
      v.dirty = true;
    }
    const { moved } = endDrag(this.state);
    if (moved) this.afterMove();
    if (this.state.status === "lost") this.scheduleEnd();
  }

  private afterMove(): void {
    this.updateHud();
    track("move", { level: this.level.n, moves: this.state.moves });
    const h = this.bar.value;
    this.tweens.add({ targets: h, scale: 1.18, duration: 90, yoyo: true });
  }

  // ------------------------------------------------------------------ feedback

  private stepFx(): void {
    const now = this.time.now;
    if (now - this.lastTick < 55) return;
    this.lastTick = now;
    sfx.goalTick();
  }

  private blockedFx(worm: number, reason: StepFail, dir: Dir, lead: Cell): void {
    const now = this.time.now;
    if (now - this.lastNope < 260) return;
    this.lastNope = now;
    sfx.nope();
    const v = this.views.get(worm);
    if (v) {
      v.lean.x = DX[dir] * this.cell * 0.14;
      v.lean.y = DY[dir] * this.cell * 0.14;
      v.dirty = true;
    }
    if (reason === "color") {
      const gk = (lead.y * this.level.cols + lead.x) * 4 + dir;
      const gb = this.gateObjs.get(gk);
      if (gb) this.tweens.add({ targets: gb, scaleX: 1.12, scaleY: 0.86, duration: 70, yoyo: true, repeat: 1 });
      const c = this.center(lead.x, lead.y);
      floatText(this, c.x + DX[dir] * this.cell * 0.9, c.y + DY[dir] * this.cell * 0.9, t("otherColor"), "#ff8a8a", 26);
    } else if (reason === "stone") {
      const nx = lead.x + DX[dir];
      const ny = lead.y + DY[dir];
      const sb = this.stoneObjs.get(ny * this.level.cols + nx);
      if (sb) this.tweens.add({ targets: sb, angle: 8, duration: 60, yoyo: true, repeat: 1, onComplete: () => sb.setAngle(0) });
    }
  }

  private burst(x: number, y: number, color: number, n = 14, speed = 1): void {
    for (let i = 0; i < n; i++) {
      const dot = this.add.image(x, y, "kacis-dot").setTint(color).setDepth(6).setScale(Phaser.Math.FloatBetween(0.4, 0.9));
      const a = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const dist = Phaser.Math.FloatBetween(34, 92) * speed;
      this.tweens.add({
        targets: dot,
        x: x + Math.cos(a) * dist,
        y: y + Math.sin(a) * dist,
        scale: 0,
        alpha: 0.2,
        duration: Phaser.Math.Between(320, 560),
        ease: "Quad.Out",
        onComplete: () => dot.destroy(),
      });
    }
  }

  // ------------------------------------------------------------------ escape

  private onGestureExit(info: EscapeInfo, end: End, dir: Dir): void {
    const v = this.views.get(info.worm);
    const s = this.state;
    this.drag = null;
    this.afterMove();
    const order = s.order.length - 1;
    const color = COLORS[info.color]!;
    if (v) {
      v.lean.x = 0;
      v.lean.y = 0;
      const cells = info.cells;
      const L = cells.length;
      const back2front: Pt[] = [];
      const leadHead = end === "head";
      for (let i = 0; i < L; i++) {
        const c = leadHead ? cells[L - 1 - i]! : cells[i]!;
        back2front.push(this.center(c.x, c.y));
      }
      const lead = back2front[L - 1]!;
      const path = back2front.slice();
      for (let k = 1; k <= L + 3; k++) path.push({ x: lead.x + DX[dir] * this.cell * k, y: lead.y + DY[dir] * this.cell * k });
      v.exit = { path, off: 0, leadHead, dir, lead };
      const total = L * this.cell + this.cell * 0.2;
      const holder = { off: 0 };
      sfx.rocket();
      this.tweens.add({
        targets: holder,
        off: total,
        duration: 170 + 105 * L,
        ease: "Quad.In",
        onUpdate: () => {
          if (v.exit) v.exit.off = holder.off;
          v.dirty = true;
        },
        onComplete: () => {
          v.gfx.destroy();
          v.letter.destroy();
          this.views.delete(info.worm);
          this.popAt(lead, dir, color, info, order);
        },
      });
    }
    // stones dissolve
    for (const c of info.dissolved) this.dissolveStone(c);
    this.updateHud();
    if (info.won) this.pendingEnd++;
    this.scheduleEnd();
  }

  private popAt(lead: Pt, dir: Dir, color: number, info: EscapeInfo, order: number): void {
    const gx = lead.x + DX[dir] * this.cell * 0.7;
    const gy = lead.y + DY[dir] * this.cell * 0.7;
    sfx.pop(Math.min(9, 3 + order));
    this.burst(gx, gy, color, 16);
    const gb = this.gateObjs.get((this.cellOf(lead).y * this.level.cols + this.cellOf(lead).x) * 4 + dir);
    if (gb) this.tweens.add({ targets: gb, scaleX: 1.2, scaleY: 1.2, duration: 90, yoyo: true, ease: "Quad.Out" });
    this.cameras.main.shake(80, 0.0035);
    // the letter flies into the tray
    const slot = this.slots[order];
    const t = this.add
      .text(gx, gy, info.letter, { fontFamily: FONT, fontSize: `${Math.round(this.cell * 0.5)}px`, color: "#ffffff", fontStyle: "bold", stroke: "#1b1f3a", strokeThickness: 7 })
      .setOrigin(0.5)
      .setDepth(8)
      .setScale(0.4);
    this.tweens.add({ targets: t, scale: 1.1, duration: 140, ease: "Back.Out" });
    if (slot)
      this.tweens.add({
        targets: t,
        x: slot.x,
        y: slot.y,
        delay: 150,
        duration: 380,
        ease: "Cubic.InOut",
        onComplete: () => {
          t.destroy();
          this.fillSlot(order, info.letter, color);
          sfx.goalTick();
          this.refreshTrayLabel();
          this.checkWordProgress();
        },
      });
    else t.destroy();
  }

  private cellOf(p: Pt): Cell {
    return { x: Math.round((p.x - this.ox) / this.cell - 0.5), y: Math.round((p.y - this.oy) / this.cell - 0.5) };
  }

  private checkWordProgress(): void {
    const s = this.state;
    if (s.status !== "won" || this.slots.length === 0) return;
    this.refreshTrayLabel();
    if (wordStar(s)) {
      for (const sl of this.slots) {
        sl.bg.lineStyle(5, GOLD, 1).strokeRoundedRect(sl.x - 42, sl.y - 46, 84, 92, 18);
        this.tweens.add({ targets: sl.t, scale: 1.2, duration: 160, yoyo: true, delay: this.slots.indexOf(sl) * 70 });
      }
      this.trayLabel.setScale(0.6);
      this.tweens.add({ targets: this.trayLabel, scale: 1, duration: 260, ease: "Back.Out" });
    }
  }

  private dissolveStone(c: Cell): void {
    const id = c.y * this.level.cols + c.x;
    const box = this.stoneObjs.get(id);
    if (!box) return;
    this.stoneObjs.delete(id);
    const p = this.center(c.x, c.y);
    this.time.delayedCall(260, () => {
      sfx.special();
      this.burst(p.x, p.y, 0xd4a276, 14, 0.8);
      this.tweens.add({ targets: box, scale: 0, angle: 25, alpha: 0, duration: 260, ease: "Back.In", onComplete: () => box.destroy() });
    });
    this.save.tipStone = true;
    writeSave(ID, this.save);
  }

  // ------------------------------------------------------------------ end of level

  private scheduleEnd(): void {
    if (this.finished) return;
    const s = this.state;
    if (s.status === "playing") return;
    this.finished = true;
    // let the exit animation and the tray flight play out
    const delay = s.status === "won" ? 1150 : this.views.size && [...this.views.values()].some((v) => v.exit) ? 700 : 250;
    this.time.delayedCall(delay, () => (s.status === "won" ? this.onWon() : this.onLost()));
  }

  private secs(): number {
    return Math.round((Date.now() - this.startedAt) / 1000);
  }

  private onWon(): void {
    const s = this.state;
    const lv = this.level;
    const star = wordStar(s);
    track("level_end", { level: lv.n, result: "won", moves: s.moves, secs: this.secs() });
    if (star) track("moment", { level: lv.n, type: "word_star" });
    if (s.moves === s.budget) track("moment", { level: lv.n, type: "last_move_win" });
    delete this.save.fails[String(lv.n)];
    const starAwarded = star && !this.save.starLevels.includes(lv.n);
    if (starAwarded) {
      this.save.starLevels.push(lv.n);
      this.save.stars++;
    }
    this.save.level = lv.n + 1;
    writeSave(ID, this.save);
    const stars = starsFor(s);
    const outcome = recordWin(ID, lv.n, stars);
    sfx.win();
    confetti(this);
    const escaped = t("escaped", { jellies: lv.worms.length, moves: s.moves });
    const sub = `${starLine(stars)}\n` + (star
      ? `${escaped}\n${t("starWord", { word: lv.word, award: starAwarded ? t("starNew") : t("starOld") })}`
      : `${escaped}\n${t("missedWord", { word: lv.word })}`);
    this.overlayBox = overlay(this, star ? t("wonStar") : t("wonAll"), sub, [
      { label: t("share"), color: 0x1982c4, onClick: () => void this.shareResult(star) },
      { label: mapText("continue"), color: 0x8ac926, onClick: () => openMap(this, ID, afterWin(lv.n, outcome)) },
    ]);
  }

  private onLost(): void {
    const lv = this.level;
    const fails = (this.save.fails[String(lv.n)] ?? 0) + 1;
    this.save.fails[String(lv.n)] = fails;
    writeSave(ID, this.save);
    track("level_end", { level: lv.n, result: "lost", moves: this.state.moves, secs: this.secs(), fails });
    sfx.lose();
    const buttons = [{ label: t("retry"), color: 0x1982c4, onClick: () => this.retry(true) }];
    if (canSkip(fails)) buttons.push({ label: t("skip"), color: 0x6a4c93, onClick: () => this.skip() });
    this.overlayBox = overlay(this, t("lost"), canSkip(fails) ? t("lostSkip") : t("lostRetry"), buttons);
  }

  private retry(afterLoss = false): void {
    if (!afterLoss && this.state.status === "playing" && this.state.moves > 0) track("retry", { level: this.level.n });
    this.startLevel(this.level.n);
  }

  /** H4: skip is free once the level failed 3 times. */
  private skip(): void {
    const n = this.level.n;
    const fails = this.save.fails[String(n)] ?? 0;
    if (!canSkip(fails)) return;
    track("level_end", { level: n, result: "skip", moves: this.state.moves, secs: this.secs() });
    delete this.save.fails[String(n)];
    this.save.level = n + 1;
    writeSave(ID, this.save);
    openMap(this, ID, afterSkip(n, recordSkip(ID, n)));
  }

  // ------------------------------------------------------------------ share

  private async shareResult(star: boolean): Promise<void> {
    const lv = this.level;
    const s = this.state;
    track("share_click", { level: lv.n, star });
    const body = t(star ? "shareStar" : "shareNoStar", { n: lv.n, jellies: lv.worms.length, moves: s.moves });
    const file = await this.shareCard(star);
    const ok = await share(body, SHARE_URL, t("title"), file);
    if (ok) track("share_done", { level: lv.n, star });
  }

  /** 1080² card without spoilers: coloured chips for the worms (no letters), moves and the star. */
  private shareCard(star: boolean): Promise<File | null> {
    return new Promise((resolve) => {
      try {
        const cv = document.createElement("canvas");
        cv.width = 1080;
        cv.height = 1080;
        const ctx = cv.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.fillStyle = "#1b1f3a";
        ctx.fillRect(0, 0, 1080, 1080);
        ctx.textAlign = "center";
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 120px system-ui, sans-serif";
        ctx.fillText(t("title"), 540, 240);
        ctx.fillStyle = "#8f96c2";
        ctx.font = "48px system-ui, sans-serif";
        ctx.fillText(t("cardLevel", { n: this.level.n }), 540, 320);
        const n = this.level.worms.length;
        const sz = 120;
        const gap = 26;
        const x0 = 540 - (n * sz + (n - 1) * gap) / 2;
        this.level.worms.forEach((w, i) => {
          ctx.fillStyle = `#${COLORS[w.color]!.toString(16).padStart(6, "0")}`;
          ctx.beginPath();
          ctx.roundRect(x0 + i * (sz + gap), 470, sz, sz, 36);
          ctx.fill();
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 70px system-ui, sans-serif";
          ctx.fillText(star ? "?" : "·", x0 + i * (sz + gap) + sz / 2, 470 + sz * 0.7);
        });
        ctx.fillStyle = star ? "#ffd166" : "#b8bee6";
        ctx.font = "bold 110px system-ui, sans-serif";
        ctx.fillText(star ? t("cardWord") : t("cardMoves", { moves: this.state.moves }), 540, 800);
        ctx.fillStyle = "#8f96c2";
        ctx.font = "44px system-ui, sans-serif";
        ctx.fillText(star ? t("cardInMoves", { moves: this.state.moves }) : t("cardAsk"), 540, 880);
        ctx.fillText(t("cardFair"), 540, 990);
        cv.toBlob((b) => resolve(b ? new File([b], "kacis.png", { type: "image/png" }) : null), "image/png");
      } catch {
        resolve(null);
      }
    });
  }

  // ------------------------------------------------------------------ bot hook

  private snapshot(): unknown {
    const s = this.state;
    return {
      level: this.level.n,
      cols: this.level.cols,
      rows: this.level.rows,
      cell: this.cell,
      word: this.level.word,
      hint: this.level.hint,
      budget: s.budget,
      moves: s.moves,
      movesLeft: movesLeft(s),
      escaped: s.escaped,
      order: s.order.join(""),
      status: s.status,
      star: wordStar(s),
      worms: s.worms.map((w) => ({ id: w.id, letter: w.letter, color: w.color, alive: w.alive, cells: w.cells })),
      board: { ox: this.ox, oy: this.oy },
    };
  }

  /** Play the solver's moves on the live scene (~300 ms per move); resolves when the level ends. */
  private autoplay(): Promise<void> {
    if (this.autoplaying) return Promise.resolve();
    this.autoplaying = true;
    const sleep = (ms: number) => new Promise<void>((r) => this.time.delayedCall(ms, () => r()));
    const run = async (): Promise<void> => {
      try {
        for (let guard = 0; guard < 80 && this.state.status === "playing" && !this.overlayBox; guard++) {
          const plan = planFrom(this.state);
          if (!plan || !plan.length) break;
          for (const g of plan) {
            if (this.state.status !== "playing") break;
            await this.playGesture(g, sleep);
            await sleep(300);
          }
          if (this.state.status !== "playing") break;
        }
      } finally {
        this.autoplaying = false;
      }
    };
    return run();
  }

  private async playGesture(g: Gesture, sleep: (ms: number) => Promise<void>): Promise<void> {
    const s = this.state;
    if (!beginDrag(s, g.worm, g.end)) return;
    this.drag = { pointerId: -1, worm: g.worm, end: g.end, grab: { x: 0, y: 0 } };
    const v = this.views.get(g.worm);
    if (v) v.dirty = true;
    for (const d of g.steps) {
      await sleep(95);
      const r = dragStep(s, d);
      if (!r.outcome.ok) break;
      if (r.outcome.kind === "move") this.stepFx();
      if (r.gestureEnded && r.escape) {
        this.onGestureExit(r.escape, g.end, d);
        return;
      }
    }
    this.releaseDrag();
  }
}
